import { inject, observer } from "mobx-react";
import React from "react";
import { Redirect, Route, RouteProps } from "react-router-dom";
import { RoomSidebar } from "./components/roomSidebar/roomSidebar";
import {
  AcceptInvite,
  Components,
  CreateAccount,
  Dashboard,
  Documents,
  ForgotPassword,
  Logs,
  Login,
  Manual,
  Messenger,
  Notifications,
  NotificationSettings,
  ProjectSettings,
  ResetPassword,
  Room,
  RoomSettings,
  UserInvite,
  UserProfile,
  UserSettings,
  VerifyAccount,
  Workspaces,
  WorkSpaceSettings
} from "./pages";

import { Faq } from "./pages/faq";
import { MultiLogin } from "./pages/multiLogin";
import { NewsfeedSettings } from "./pages/user/newsfeedSettings";
import { Videos } from "./pages/videos";
import { Feedback, LoadingView, NavBar, SpaceSettingsModal } from "./views";

export const base_URL: string = process.env.BASE_URL

export interface MatchParams {
  id?:      string;
  view?:    string;
  user_id?: string;
  item_id?: string;
  token?:   string;
  type?:    string;
}

export enum Page {
  Components        = "components",
  CreateAccount     = "create-account",
  Dashboard         = "",
  Documents         = "room",
  Default           = "room",
  Faq               = "faq",
  ForgotPassword    = "forgot-password",
  GridView          = "gridview",
  Home              = "",
  Login             = "login",
  Manual            = "manual",
  Messenger         = "messenger",
  MultiLogin        = "multi-login",
  Notifications     = "user/notifications",
  ProjectSettings   = "project",
  ResetPassword     = "reset-password",
  Room              = "room",
  RoomLog           = "room",
  RoomSettings      = "room",
  Settings          = "settings",
  UserInvite        = "user/invite",
  UserProfile       = "profile",
  UserSettings      = "user/settings",
  VerifyAccount     = "verify-account",
  Videos            = "videos",
  Workspaces        = "workspaces",
  WorkSpaceLog      = "workspace",
  WorkSpaceSettings = "workspace",
}

export enum RoomView {
  Question    = "question",
  Deliverable = "deliverable",
  Milestone   = "milestone",
  Meeting     = "meeting",
}
export enum LogView {
  All      = "all",        // not used
  Question = "question",
  Decision = "decision",
  Action   = "action",
  Meeting  = "meeting",    // not used
}

export const getRoutePath = (
  page: Page,
  params?: {
    id?:      number;
    view?:    string;
    user_id?: string;
    item_id?: string;
    token?:   string;
    type?:    string;
  }
): string => {
  return (
    "/" +
    page +
    (params && params.id ? "/" + params.id : "") +
    (params && params.view ? "/" + params.view : "") +
    (params && params.user_id ? "/" + params.user_id : "") +
    (params && params.item_id ? "/" + params.item_id : "") +
    (params && params.token ? "/" + params.token : "") +
    (params && params.type ? "/type/" + params.type : "")
  );
};

export const publicRoutes: RouteProps[] = [
  {
    path: "/login",
    component: Login
  }, {
    path: "/forgot-password",
    component: ForgotPassword
  }, {
    path: "/reset-password/:token",
    component: ResetPassword
  }, {
    path: "/verify-account/:token",
    component: VerifyAccount
  }, {
    path: "/create-account",
    component: CreateAccount
  }, {
    path: "/join/:token",
    component: AcceptInvite
  }
];

export const privateRoutes: RouteProps[] = [
  {
    path: "/manual",
    component: Manual
  }, {
    path: "/multi-login",
    component: MultiLogin
  }, {
    exact: true,
    path: "/",
    component: Dashboard
  },
  {
    path: "/workspaces",
    component: Workspaces,
  }
  , {
    path: "/workspace/:id",
    component: WorkSpaceSettings
  }, {
    path: "/project/:id/settings",
    component: ProjectSettings
  }, {
    path: "/room/:id/:view/type/:type",
    component: Logs
  }, {
    path: "/room/:id/:view/:item_id",
    component: Room
  }, {
    path: "/room/:id/settings",
    component: RoomSettings
  },
  {
    path: "/project/:id/documents",
    component: Room
  },
  {
    path: "/project/:id/logs",
    component: Logs
  }, {
    path: "/room/:id/:view",
    component: Room
  }, {
    path: "/room/:id",
    component: RoomSettings
  }, {
    path: "/components",
    component: Components
  }, {
    path: "/profile/:user_id",
    component: UserProfile
  }, {
    path: "/user/settings",
    component: UserSettings
  }, {
    path: "/user/notification-settings",
    component: NotificationSettings
  }, {
    path: "/user/newsfeed-settings",
    component: NewsfeedSettings
  }, {
    path: "/user/invite/:id/:view",
    component: UserInvite
  }, {
    path: "/user/notifications",
    component: Notifications
  }, {
    path: "/messenger",
    component: Messenger
  }, {
    path: "/faq",
    component: Faq
  }, {
    path: "/videos",
    component: Videos
  }
];

export interface LocationState {
  from: string;
}

export const PublicRoute = (props: any): JSX.Element => {
  const { component, computedMatch, ...rest } = props;

  return (
    <Route {...rest}>
      <NavBar />
      {React.createElement(component, computedMatch)}
    </Route>
  );
};

// TODO figure out prop types here
export const PrivateRoute = inject("rootStore", "authStore")(observer((props: any): JSX.Element => {
  const {
    redirectTo,
    rootStore,
    authStore,
    component,
    computedMatch,
    ...rest
  } = props;

  if (authStore.isLoggedIn) rootStore.spaceStore.changeSpace(computedMatch?.params?.id);

  return authStore.isAuthenticating ? (
    <LoadingView />
  ) : authStore.isLoggedIn ? (
    authStore.hasWorkspace ?
      <Route {...rest}>
        <RoomSidebar match={computedMatch}>
          <NavBar key={'navbar'} match={computedMatch} />
          {React.createElement(component, { key: computedMatch.url, ...computedMatch })}
          <Feedback key={'feedback'} />
          <SpaceSettingsModal key={'space_settings'} />
        </RoomSidebar>
      </Route>
      : (
        <Workspaces />
      )
  ) : (
    <Redirect to={{ pathname: redirectTo || "/", state: { from: props.location.pathname } }} />
  );
}));
